import { useMemo } from 'react';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { getFullName } from '@frontend/name';
import { FilterableCheckboxListItem } from '../../components/filterable-checkbox-list/use-filterable-checkbox-list';
import { useSchedulingLocationInfo } from '../use-scheduling-location-info';
import { useGetProvidersV3 } from './use-get-calendar-v3-queries-data';
import { useGetOperatoryV3Data } from './use-get-operatory-v3-data';

type UseGetLocationOperatoryProviderListProps = {
  locationIds: string[];
};

export const useGetLocationOperatoryProviderList = ({ locationIds }: UseGetLocationOperatoryProviderListProps) => {
  const { getScopeName } = useSchedulingLocationInfo();
  const { data: providersData } = useGetProvidersV3(locationIds);
  const { data: operatoryData } = useGetOperatoryV3Data({
    selectedLocationId: locationIds[0],
    selectedLocationIds: locationIds,
    pageConfig: { page: 1, limit: SchedulerV3.Constants.CALENDAR_VIEW_V3_API_QUERY_LIMIT },
  });

  const isMultiLocation = locationIds.length > 1;

  const providerList = useMemo<FilterableCheckboxListItem[]>(() => {
    const allProviders = (providersData?.providers ?? []).map((practitioner) => ({
      label: practitioner.displayName || getFullName(practitioner),
      value: practitioner.id,
      isArchived: practitioner.isArchived,
      isHidden: practitioner.hidden,
      locationName: isMultiLocation ? getScopeName(practitioner.locationId) : '',
    }));

    // Remove duplicates, then sort by isArchived (false first), then by label
    return Array.from(new Map(allProviders.map((p) => [p.value, p])).values()).sort((a, b) => {
      // Normalize undefined/null to false for consistent sorting
      const aArchived = !!a.isArchived;
      const bArchived = !!b.isArchived;

      if (aArchived !== bArchived) {
        return Number(aArchived) - Number(bArchived); // false (active) before true (archived)
      }
      return a.label.localeCompare(b.label);
    });
  }, [providersData?.providers, getScopeName, isMultiLocation]);

  const operatoryList = useMemo<FilterableCheckboxListItem[]>(() => {
    const allOperatories = (operatoryData?.operatories ?? []).map((operatory) => ({
      label: operatory.displayName || operatory.externalName || '',
      value: operatory.id,
      locationName: isMultiLocation ? getScopeName(operatory.locationId) : '',
    }));

    // Ignore duplicate operatories across locations and sort by name
    return Array.from(new Map(allOperatories.map((operatory) => [operatory.value, operatory])).values()).sort((a, b) =>
      a.label.localeCompare(b.label)
    );
  }, [operatoryData?.operatories, isMultiLocation, getScopeName]);

  return { providersData, operatoryData, providerList, operatoryList };
};
